import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Briefcase, PiggyBank, Compass, Leaf, Fuel, Gauge, Wrench, FileText, Cpu, Monitor, TrendingUp, Shield, BarChart3, Anchor } from "lucide-react";
import segmentFerry from "@/assets/brim_explorer-2.png";
import segmentTug from "@/assets/segment-tug.jpg";
import segmentMerchant from "@/assets/segment-merchant-new.jpg";
import segmentFishing from "@/assets/fishing-cover.png";
import segmentCtv from "@/assets/segment-ctv-new.jpg";
import brimLogo from "@/assets/logo-brim-explorer.png";
import alilauroLogo from "@/assets/alilauro_logo.png";
import logoAesen from "@/assets/logo-aesen.png";
import logoStromma from "@/assets/logo-stromma.png";
import logoErikThun from "@/assets/logo-erik-thun.png";
import logoMfShipping from "@/assets/logo-mf-shipping.png";
import logoBergenTankers from "@/assets/logo-bergen-tankers.png";
import logoMhoCo from "@/assets/logo-mho-co.png";
import logoSvitzer from "@/assets/logo-svitzer.png";
import logoPsaMarine from "@/assets/logo-psa-marine.png";
import logoKstMaritime from "@/assets/logo-kst-maritime.png";
import logoSjofartsverket from "@/assets/logo-sjofartsverket.png";
import logoEgilUlvan from "@/assets/logo-egil-ulvan.png";
import logoBlidosund from "@/assets/logo-blidosund.png";
import logoLauro from "@/assets/logo-lauro.png";
import segmentPilot from "@/assets/segment-pilot.jpg";
import ihelmBridgeDisplay from "@/assets/ihelm-bridge-display.png";
import ihelmSignalsDashboard from "@/assets/ihelm-signals-dashboard.png";
import ihelmFleetMap from "@/assets/ihelm-on-vessel-2.png";
import ctvVessel from "@/assets/ctv-vessel.png";
import tugDigitalTwin from "@/assets/tug-wire-blue.png";
import pilotDigitalTwin from "@/assets/pilot-vessel.jpg";
import digitalTwinBranded from "@/assets/digital-twin-branded.png";

const roleIcons: Record<string, typeof Briefcase> = {
  "CEO / Fleet Owner": Briefcase,
  "CFO": PiggyBank,
  "Fleet Manager": Compass,
  "Sustainability Manager": Leaf,
};

const benefitIcons: Record<string, typeof Briefcase> = {
  Fuel, Gauge, Wrench, FileText, TrendingUp, Leaf, Shield, BarChart3, Monitor, Cpu,
};

const stepIcons: Record<string, typeof Briefcase> = {
  Cpu, Monitor, TrendingUp, Wrench, Shield,
};

// Static data that doesn't need translation (images, logos, icons per segment)
const segmentStaticData: Record<string, {
  image: string;
  benefitIconKeys: string[];
  stepIconKeys: string[];
  howItWorksImage?: string;
  digitalTwinImage?: string;
  trustedLogos?: { name: string; logo: string }[];
}> = {
  ferry: {
    image: segmentFerry,
    benefitIconKeys: ["Fuel", "Gauge", "Wrench", "FileText"],
    stepIconKeys: ["Cpu", "Monitor", "TrendingUp"],
    howItWorksImage: ihelmBridgeDisplay,
    digitalTwinImage: digitalTwinBranded,
    trustedLogos: [
      { name: "Brim Explorer", logo: brimLogo },
      { name: "Alilauro", logo: alilauroLogo },
      { name: "Blidösund", logo: logoBlidosund },
      { name: "Lauro.it", logo: logoLauro },
    ],
  },
  tug: {
    image: segmentTug,
    benefitIconKeys: ["Fuel", "TrendingUp", "FileText", "Gauge"],
    stepIconKeys: ["Cpu", "TrendingUp", "Wrench"],
    howItWorksImage: tugDigitalTwin,
    trustedLogos: [
      { name: "Svitzer", logo: logoSvitzer },
      { name: "PSA Marine", logo: logoPsaMarine },
      { name: "KST Maritime", logo: logoKstMaritime },
    ],
  },
  merchant: {
    image: segmentMerchant,
    benefitIconKeys: ["TrendingUp", "FileText", "Leaf", "Fuel", "Gauge"],
    stepIconKeys: ["Cpu", "Monitor", "TrendingUp"],
    howItWorksImage: ihelmFleetMap,
    trustedLogos: [
      { name: "Erik Thun", logo: logoErikThun },
      { name: "MF Shipping Group", logo: logoMfShipping },
      { name: "Egil Ulvan Rederi", logo: logoEgilUlvan },
    ],
  },
  fishing: {
    image: segmentFishing,
    benefitIconKeys: ["Fuel", "Gauge", "Wrench", "Monitor"],
    stepIconKeys: ["Cpu", "TrendingUp", "Wrench"],
    howItWorksImage: ihelmSignalsDashboard,
    trustedLogos: [],
  },
  ctv: {
    image: segmentCtv,
    benefitIconKeys: ["Shield", "FileText", "Fuel", "Gauge", "BarChart3", "Monitor"],
    stepIconKeys: ["Shield", "TrendingUp", "Wrench"],
    howItWorksImage: ctvVessel,
    trustedLogos: [
      { name: "Aesen", logo: logoAesen },
      { name: "MHO-Co", logo: logoMhoCo },
    ],
  },
  pilot: {
    image: segmentPilot,
    benefitIconKeys: ["Shield", "FileText", "Fuel", "Gauge", "BarChart3", "Monitor"],
    stepIconKeys: ["Cpu", "TrendingUp", "Wrench"],
    howItWorksImage: pilotDigitalTwin,
    trustedLogos: [
      { name: "Sjöfartsverket", logo: logoSjofartsverket },
    ],
  },
};

const VALID_SEGMENTS = ["ferry", "tug", "merchant", "fishing", "ctv", "pilot"];

const Segment = () => {
  
  const { t, i18n } = useTranslation('segments');
  const { slug } = useParams<{ slug: string }>();
  const staticData = slug ? segmentStaticData[slug] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug || !VALID_SEGMENTS.includes(slug) || !staticData) return <Navigate to="/404" replace />;

  // Helper to read translated segment content
  const s = (key: string) => t(`${slug}.${key}`);
  const sArr = (key: string, count: number) => {
    const items = [];
    for (let i = 0; i < count; i++) {
      items.push(t(`${slug}.${key}.${i}`, { defaultValue: '' }));
    }
    return items.filter(Boolean);
  };

  const title = s('title');
  const headline = s('headline');
  const description = s('description');
  const metaTitle = s('metaTitle');
  const metaDescription = s('metaDescription');
  const benefitsHeadline = s('benefitsHeadline');
  const cetafuelNote = s('cetafuelNote');

  // Read keyBenefits array
  const keyBenefits = staticData.benefitIconKeys.map((iconKey, i) => ({
    icon: benefitIcons[iconKey] || Fuel,
    title: t(`${slug}.keyBenefits.${i}.title`, { defaultValue: '' }),
    description: t(`${slug}.keyBenefits.${i}.description`, { defaultValue: '' }),
  })).filter(b => b.title);

  // Read howItWorks
  const howItWorksIntro = t(`${slug}.howItWorks.intro`, { defaultValue: '' });
  const howItWorksDetails = t(`${slug}.howItWorks.details`, { defaultValue: '' });
  const howItWorksSteps = staticData.stepIconKeys.map((iconKey, i) => ({
    title: t(`${slug}.howItWorks.steps.${i}`, { defaultValue: '' }),
    icon: stepIcons[iconKey] || Cpu,
  })).filter(s => s.title);

  // Read digitalTwin (only ferry has it)
  const hasDigitalTwin = slug === 'ferry';
  const digitalTwin = hasDigitalTwin ? {
    title: t(`${slug}.digitalTwin.title`, { defaultValue: '' }),
    description: t(`${slug}.digitalTwin.description`, { defaultValue: '' }),
    extra: t(`${slug}.digitalTwin.extra`, { defaultValue: '' }),
    image: staticData.digitalTwinImage,
  } : null;

  // Read roles
  const roleKeys = ["CEO / Fleet Owner", "CFO", "Fleet Manager", "Sustainability Manager"];
  const rolesRaw = t(`${slug}.roles`, { returnObjects: true }) as any[];
  const roles = Array.isArray(rolesRaw) ? rolesRaw : [];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://cetasol.com/segments/${slug}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://cetasol.com/segments/${slug}`} />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": title, "item": `https://cetasol.com/segments/${slug}` }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <section className="relative min-h-screen flex items-center pb-24 pt-24 overflow-hidden">
          <div className="absolute inset-0">
            <img src={staticData.image} alt={`${title} vessel`} className="w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-background/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/30 to-background" />
          </div>
          <div className="container relative z-10 mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} className="max-w-2xl">
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">
                {title} {t('segment')}
              </p>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">{headline}</h1>
              <p className="text-base md:text-lg text-foreground mb-10 max-w-lg leading-relaxed">{description.split('\n\n')[0]}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors">
                {t('contactUs')} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Key Benefits */}
        {keyBenefits.length > 0 && (
          <section className="py-20 section-fade mb-20">
            <div className="container mx-auto px-6 max-w-5xl">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-12 text-center">
                <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('keyBenefits')}</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                  {benefitsHeadline || t('whatIhelmDelivers', { segment: title.toLowerCase() })}
                </h2>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                {keyBenefits.map((benefit, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="p-8">
                    <benefit.icon size={22} className="text-primary mb-5" />
                    <h3 className="font-display text-lg font-semibold text-foreground mb-3">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* How iHelm Works */}
        {howItWorksIntro && (
          <section className="bg-surface-light py-16 mb-0">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="text-center mb-10">
                <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">{t('howItWorksLabel')}</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">{t('howItWorksHeading')}</h2>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto mb-12">
                <div>
                  <p className="text-muted-foreground leading-relaxed mb-4">{howItWorksIntro}</p>
                  <p className="text-muted-foreground leading-relaxed mb-6">{howItWorksDetails}</p>
                  <Link to="/ihelm-intelligent-platform" className="inline-flex items-center gap-2 text-sm text-foreground hover:text-foreground/80 transition-colors font-medium">
                    {t('learnMoreIhelm')} <ArrowRight size={14} />
                  </Link>
                </div>
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.15 }} className="overflow-hidden">
                  <img src={staticData.howItWorksImage || ihelmBridgeDisplay} alt={`iHelm dashboard for ${title.toLowerCase()} operations`} className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 max-w-3xl mx-auto divide-y md:divide-y-0">
                {howItWorksSteps.map((step, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="p-6 text-center border-r last:border-r-0 border-border">
                    <step.icon size={24} className="text-primary mb-4 mx-auto" />
                    <h3 className="font-display font-semibold text-foreground">{step.title}</h3>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Digital Twin */}
        {digitalTwin && digitalTwin.title && (
          <section className="section-fade py-28 md:py-36 mb-0">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="text-center mb-14">
                <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('digitalTwinLabel')}</p>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">{digitalTwin.title}</h2>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
                <div>
                  <p className="text-muted-foreground leading-relaxed mb-4">{digitalTwin.description}</p>
                  {digitalTwin.extra && <p className="text-muted-foreground leading-relaxed">{digitalTwin.extra}</p>}
                </div>
                {digitalTwin.image && (
                  <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                    <img src={digitalTwin.image} alt={`Digital twin visualization for ${title.toLowerCase()} operations`} className="w-full mix-blend-lighten" loading="lazy" />
                  </motion.div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* CetaFuel cross-sell */}
        {cetafuelNote && (
          <section className="bg-surface-light py-28 md:py-36 mb-20">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center max-w-3xl mx-auto">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">{t('virtualFlowmeter')}</h2>
                <p className="text-muted-foreground mb-6">{cetafuelNote}</p>
                <Link to="/cetafuel" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors">
                  {t('learnMoreCetafuel')} <ArrowRight size={16} />
                </Link>
              </motion.div>
            </div>
          </section>
        )}

        {/* Who Benefits */}
        {roles.length > 0 && (
          <section className="py-20 section-fade mb-20">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-12 text-center">
                <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('whoBenefitsLabel')}</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">{t('whoBenefitsHeading')}</h2>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                {roles.map((roleData: any, i: number) => {
                  const Icon = roleIcons[roleData.role] || Briefcase;
                  return (
                    <motion.div
                      key={roleData.role}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.08 }}
                      className={`p-8 ${i % 2 === 0 ? 'md:border-r border-border' : ''} ${i < roles.length - 2 ? 'border-b border-border' : ''} ${i === roles.length - 2 && roles.length % 2 === 0 ? '' : i < roles.length - 1 ? 'border-b border-border md:border-b-0' : ''}`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <Icon size={20} className="text-primary" />
                        <span className="font-display font-bold text-foreground text-sm tracking-wide uppercase">{roleData.role}</span>
                      </div>
                      <h3 className="font-display text-lg font-semibold text-foreground mb-4">{roleData.headline}</h3>
                      <ul className="space-y-2.5">
                        {(roleData.benefits || []).map((benefit: string, j: number) => (
                          <li key={j} className="flex items-start gap-2.5">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                            <span className="text-sm text-muted-foreground leading-relaxed">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* CTA + Segment Navigation */}
        <section className="bg-surface-light py-24">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-16">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
                {t('seeHow', { segment: title.toLowerCase() })}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">{t('bookDemoDesc')}</p>
              <Link to="/book-a-demo" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors">
                {t('getStarted')} <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center">
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-8">{t('exploreOther')}</p>
              <div className="flex flex-wrap justify-center gap-3">
                {VALID_SEGMENTS
                  .filter((s) => s !== slug)
                  .map((s) => (
                    <Link
                      key={s}
                      to={`/segments/${s}`}
                      className="px-5 py-2.5 rounded-md border border-[hsl(var(--secondary-border-light))] text-sm font-medium text-[hsl(var(--secondary-border-light))] hover:bg-secondary/18 transition-colors"
                    >
                      {t(`segmentLabels.${s}`)}
                    </Link>
                  ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Trusted By Section */}
        {staticData.trustedLogos && staticData.trustedLogos.length > 0 && (
          <section className="bg-surface-light py-20">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-12">
                <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('ourCustomers')}</p>
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">{t('trustedBy')}</h2>
              </motion.div>
              <div className="flex flex-wrap items-center gap-12">
                {staticData.trustedLogos.map((item, i) => (
                  <motion.div key={item.name} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="grayscale hover:grayscale-0 transition-all duration-300">
                    <img src={item.logo} alt={item.name} className="h-12 w-auto object-contain" loading="lazy" />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Segment;
